import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, FileText, Building2, Play, Pause, XCircle, Trash2, Calendar, CalendarOff, Send, Zap, Loader2, CheckCircle, X, Pencil } from 'lucide-react';
import { Button } from '../../components/ui/button';

const ContractList = () => {
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchParams] = useSearchParams();
  const [sendingNow, setSendingNow] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [cleanDate, setCleanDate] = useState('');
  const [addingClean, setAddingClean] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  const emptyFormData = {
    site_id: '',
    service_description: 'Weekly communal block cleaning – stairwells, landings, communal areas',
    monthly_price: '',
    vat_applicable: false,
    billing_period: 'in_advance',
    invoice_day: 1,
    payment_terms: 14,
    additional_charges: [],
    line_items: [],
    invoice_addressed_to: '',
    invoice_description: '',
    scheduling_paused: false
  };

  const [formData, setFormData] = useState({
    ...emptyFormData,
    site_id: searchParams.get('site') || ''
  });
  const [saving, setSaving] = useState(false);
  const [newCharge, setNewCharge] = useState({ description: '', amount: '', recurring: false });
  const [newLineItem, setNewLineItem] = useState({ description: '', unit_price: '', default_quantity: 1 });

  useEffect(() => {
    fetchData();
    if (searchParams.get('site')) {
      setShowForm(true);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [contractsRes, clientsRes, sitesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/contracts`),
        fetch(`${API_URL}/api/admin/clients`),
        fetch(`${API_URL}/api/admin/sites`)
      ]);
      const contractsData = await contractsRes.json();
      const clientsData = await clientsRes.json();
      const sitesData = await sitesRes.json();
      setContracts(contractsData.contracts || []);
      setClients(clientsData.clients || []);
      setSites(sitesData.sites || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        monthly_price: parseFloat(formData.monthly_price) || 0,
        additional_charges: formData.additional_charges.map(c => ({
          ...c,
          amount: parseFloat(c.amount)
        })),
        line_items: formData.line_items.map(item => ({
          ...item,
          unit_price: parseFloat(item.unit_price),
          default_quantity: parseInt(item.default_quantity) || 1
        }))
      };

      const res = await fetch(`${API_URL}/api/admin/contracts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        resetForm();
        setShowForm(false);
        fetchData();
      }
    } catch (err) {
      console.error('Failed to create contract:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingContract) return;
    setSaving(true);
    try {
      const payload = {
        site_id: formData.site_id,
        service_description: formData.service_description,
        monthly_price: parseFloat(formData.monthly_price) || 0,
        vat_applicable: formData.vat_applicable,
        billing_period: formData.billing_period,
        invoice_day: formData.invoice_day,
        payment_terms: formData.payment_terms,
        additional_charges: formData.additional_charges.map(c => ({
          ...c,
          amount: parseFloat(c.amount)
        })),
        line_items: formData.line_items.map(item => ({
          ...item,
          unit_price: parseFloat(item.unit_price),
          default_quantity: parseInt(item.default_quantity) || 1
        })),
        invoice_addressed_to: formData.invoice_addressed_to,
        invoice_description: formData.invoice_description,
        scheduling_paused: formData.scheduling_paused
      };

      const res = await fetch(`${API_URL}/api/admin/contracts/${editingContract}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setEditingContract(null);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to update contract');
      }
    } catch (err) {
      console.error('Failed to update contract:', err);
      alert('Failed to update contract');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contractId) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY DELETE this contract? This action cannot be undone.')) {
      return;
    }
    setDeleting(contractId);
    try {
      const res = await fetch(`${API_URL}/api/admin/contracts/${contractId}`, { method: 'DELETE' });
      if (res.ok) {
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to delete contract');
      }
    } catch (err) {
      console.error('Failed to delete contract:', err);
      alert('Failed to delete contract');
    } finally {
      setDeleting(null);
    }
  };

  const openEditModal = (contract) => {
    setFormData({
      site_id: contract.site_id,
      service_description: contract.service_description || '',
      monthly_price: contract.monthly_price?.toString() || '',
      vat_applicable: contract.vat_applicable || false,
      billing_period: contract.billing_period || 'in_advance',
      invoice_day: contract.invoice_day || 1,
      payment_terms: contract.payment_terms || 14,
      additional_charges: contract.additional_charges || [],
      line_items: contract.line_items || [],
      invoice_addressed_to: contract.invoice_addressed_to || '',
      invoice_description: contract.invoice_description || '',
      scheduling_paused: contract.scheduling_paused || false
    });
    setEditingContract(contract.id);
  };

  // Clean log functions
  const handleAddClean = async (contractId) => {
    if (!cleanDate) return;
    setAddingClean(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/contracts/${contractId}/cleans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: cleanDate })
      });
      if (res.ok) {
        const data = await res.json();
        setContracts(prev => prev.map(c => 
          c.id === contractId ? { ...c, completed_cleans: data.cleans } : c
        ));
        setCleanDate('');
      }
    } catch (err) {
      console.error('Failed to add clean:', err);
    } finally {
      setAddingClean(false);
    }
  };

  const handleRemoveClean = async (contractId, date) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/contracts/${contractId}/cleans/${date}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        const data = await res.json();
        setContracts(prev => prev.map(c => 
          c.id === contractId ? { ...c, completed_cleans: data.cleans } : c
        ));
      }
    } catch (err) {
      console.error('Failed to remove clean:', err);
    }
  };

  const formatCleanDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  const resetForm = () => {
    setFormData({ ...emptyFormData });
    setNewCharge({ description: '', amount: '', recurring: false });
    setNewLineItem({ description: '', unit_price: '', default_quantity: 1 });
  };

  const addCharge = () => {
    if (newCharge.description && newCharge.amount) {
      setFormData({
        ...formData,
        additional_charges: [...formData.additional_charges, { ...newCharge, amount: parseFloat(newCharge.amount) }]
      });
      setNewCharge({ description: '', amount: '', recurring: false });
    }
  };

  const removeCharge = (index) => {
    setFormData({
      ...formData,
      additional_charges: formData.additional_charges.filter((_, i) => i !== index)
    });
  };

  const addLineItem = () => {
    if (newLineItem.description && newLineItem.unit_price) {
      setFormData({
        ...formData,
        line_items: [...formData.line_items, { 
          description: newLineItem.description,
          unit_price: parseFloat(newLineItem.unit_price),
          default_quantity: parseInt(newLineItem.default_quantity) || 1
        }]
      });
      setNewLineItem({ description: '', unit_price: '', default_quantity: 1 });
    }
  };

  const removeLineItem = (index) => {
    setFormData({
      ...formData,
      line_items: formData.line_items.filter((_, i) => i !== index)
    });
  };

  const updateStatus = async (contractId, status) => {
    try {
      await fetch(`${API_URL}/api/admin/contracts/${contractId}/status?status=${status}`, { method: 'PUT' });
      fetchData();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const toggleScheduling = async (contractId, paused) => {
    try {
      await fetch(`${API_URL}/api/admin/contracts/${contractId}/scheduling?paused=${paused}`, { method: 'PUT' });
      fetchData();
    } catch (err) {
      console.error('Failed to toggle scheduling:', err);
    }
  };

  const sendNow = async (contractId) => {
    if (!window.confirm('Generate and send invoice for the current billing period?')) {
      return;
    }
    setSendingNow(contractId);
    try {
      const res = await fetch(`${API_URL}/api/admin/contracts/${contractId}/send-now`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        alert(`✓ ${data.message}`);
        fetchData();
      } else {
        alert(`Error: ${data.detail || 'Failed to send invoice'}`);
      }
    } catch (err) {
      console.error('Failed to send invoice:', err);
      alert('Failed to send invoice. Please try again.');
    } finally {
      setSendingNow(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      paused: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.active}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getSiteWithClient = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    if (!site) return { site_name: 'Unknown', company_name: 'Unknown' };
    const client = clients.find(c => c.id === site.client_id);
    return { ...site, company_name: client?.company_name || 'Unknown' };
  };

  const calculateTotalPrice = (contract) => {
    let total = contract.monthly_price || 0;
    (contract.line_items || []).forEach(item => {
      total += (item.unit_price * (item.default_quantity || 1));
    });
    return total;
  };

  // Contract Form Component (used for both create and edit)
  const ContractForm = ({ isEdit = false }) => (
    <form onSubmit={isEdit ? handleEditSubmit : handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Site *</label>
        <select
          required
          value={formData.site_id}
          onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
          className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none bg-white"
          data-testid="contract-site-select"
        >
          <option value="">Select a site...</option>
          {sites.map((site) => {
            const client = clients.find(c => c.id === site.client_id);
            return (
              <option key={site.id} value={site.id}>
                {site.site_name} ({client?.company_name || 'Unknown client'})
              </option>
            );
          })}
        </select>
        {sites.length === 0 && (
          <p className="text-sm text-gray-500 mt-1">
            No sites available. <Link to="/admin/clients" className="text-[#1a1f4e] hover:underline">Add a client first</Link>
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Service Description *</label>
        <textarea
          required
          rows={2}
          value={formData.service_description}
          onChange={(e) => setFormData({ ...formData, service_description: e.target.value })}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none resize-none"
          data-testid="contract-description-input"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Addressed To (optional)</label>
        <textarea
          rows={3}
          placeholder="e.g., Accounts Department&#10;FAO John Smith&#10;Finance Team"
          value={formData.invoice_addressed_to}
          onChange={(e) => setFormData({ ...formData, invoice_addressed_to: e.target.value })}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none resize-none"
          data-testid="contract-addressed-to-input"
        />
        <p className="text-xs text-gray-500 mt-1">Use line breaks for multiple lines. If blank, uses client's billing contact name.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Description (optional)</label>
        <textarea
          rows={4}
          placeholder="Custom description to appear on each invoice..."
          value={formData.invoice_description}
          onChange={(e) => setFormData({ ...formData, invoice_description: e.target.value })}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none resize-none"
          data-testid="contract-invoice-description-input"
        />
        <p className="text-xs text-gray-500 mt-1">This text will appear on each invoice generated from this contract.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Monthly Price (£)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.monthly_price}
            onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
            className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none"
            placeholder="0.00 if using line items only"
            data-testid="contract-price-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">VAT Applicable?</label>
          <select
            value={formData.vat_applicable ? 'yes' : 'no'}
            onChange={(e) => setFormData({ ...formData, vat_applicable: e.target.value === 'yes' })}
            className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none bg-white"
            data-testid="contract-vat-select"
          >
            <option value="no">No</option>
            <option value="yes">Yes (20%)</option>
          </select>
        </div>
      </div>

      {/* Variable Line Items Section */}
      <div className="border-t border-gray-200 pt-4 mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-3">Variable Line Items (optional)</label>
        <p className="text-xs text-gray-500 mb-3">Add items with unit prices where quantity may vary each invoice</p>
        
        {formData.line_items.length > 0 && (
          <div className="space-y-2 mb-3">
            {formData.line_items.map((item, index) => (
              <div key={index} className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2">
                <span className="flex-1 text-sm">{item.description}</span>
                <span className="text-sm">£{parseFloat(item.unit_price).toFixed(2)} × {item.default_quantity}</span>
                <span className="text-sm font-medium text-blue-700">= £{(parseFloat(item.unit_price) * item.default_quantity).toFixed(2)}</span>
                <button type="button" onClick={() => removeLineItem(index)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Description (e.g., Window cleaning per floor)"
              value={newLineItem.description}
              onChange={(e) => setNewLineItem({ ...newLineItem, description: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm"
            />
          </div>
          <div className="w-24">
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="Unit £"
              value={newLineItem.unit_price}
              onChange={(e) => setNewLineItem({ ...newLineItem, unit_price: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm"
            />
          </div>
          <div className="w-16">
            <input
              type="number"
              min="1"
              placeholder="Qty"
              value={newLineItem.default_quantity}
              onChange={(e) => setNewLineItem({ ...newLineItem, default_quantity: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm"
            />
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addLineItem} disabled={!newLineItem.description || !newLineItem.unit_price}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Billing Period</label>
          <select
            value={formData.billing_period}
            onChange={(e) => setFormData({ ...formData, billing_period: e.target.value })}
            className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none bg-white"
          >
            <option value="in_advance">In Advance</option>
            <option value="in_arrears">In Arrears</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Day</label>
          <select
            value={formData.invoice_day}
            onChange={(e) => setFormData({ ...formData, invoice_day: parseInt(e.target.value) })}
            className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none bg-white"
          >
            {[1, 7, 14, 15, 21, 28].map(day => (
              <option key={day} value={day}>{day}{day === 1 ? 'st' : day === 21 ? 'st' : 'th'} of month</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
          <select
            value={formData.payment_terms}
            onChange={(e) => setFormData({ ...formData, payment_terms: parseInt(e.target.value) })}
            className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none bg-white"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>
      </div>

      {/* Additional Charges Section */}
      <div className="border-t border-gray-200 pt-4 mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-3">Additional Charges (optional)</label>
        
        {formData.additional_charges.length > 0 && (
          <div className="space-y-2 mb-3">
            {formData.additional_charges.map((charge, index) => (
              <div key={index} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <span className="flex-1 text-sm">{charge.description}</span>
                <span className="text-sm font-medium">£{parseFloat(charge.amount).toFixed(2)}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${charge.recurring ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'}`}>
                  {charge.recurring ? 'Recurring' : 'One-time'}
                </span>
                <button type="button" onClick={() => removeCharge(index)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Description (e.g., Deep clean)"
              value={newCharge.description}
              onChange={(e) => setNewCharge({ ...newCharge, description: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm"
            />
          </div>
          <div className="w-24">
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="£"
              value={newCharge.amount}
              onChange={(e) => setNewCharge({ ...newCharge, amount: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm"
            />
          </div>
          <label className="flex items-center gap-1 text-xs whitespace-nowrap">
            <input
              type="checkbox"
              checked={newCharge.recurring}
              onChange={(e) => setNewCharge({ ...newCharge, recurring: e.target.checked })}
              className="rounded"
            />
            Forever
          </label>
          <Button type="button" size="sm" variant="outline" onClick={addCharge} disabled={!newCharge.description || !newCharge.amount}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2">One-time charges apply to next invoice only. "Forever" charges apply to all future invoices.</p>
      </div>

      {/* Auto-Scheduling Toggle */}
      <div className="border-t border-gray-200 pt-4 mt-4">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={formData.scheduling_paused}
            onChange={(e) => setFormData({ ...formData, scheduling_paused: e.target.checked })}
            className="rounded w-5 h-5"
          />
          <div>
            <span className="text-sm font-medium text-gray-700">Pause Auto-Scheduling</span>
            <p className="text-xs text-gray-500">If checked, automatic monthly invoices won't be generated for this contract</p>
          </div>
        </label>
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={saving} className="bg-[#1a1f4e] hover:bg-[#2a2f5e]" data-testid="save-contract-btn">
          {saving ? 'Saving...' : isEdit ? 'Update Contract' : 'Create Contract'}
        </Button>
        <Button type="button" variant="outline" onClick={() => {
          if (isEdit) {
            setEditingContract(null);
          } else {
            setShowForm(false);
          }
          resetForm();
        }}>Cancel</Button>
      </div>
    </form>
  );

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Contracts</h1>
          <p className="text-gray-500 text-sm mt-1">{contracts.length} contract{contracts.length !== 1 ? 's' : ''}</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#1a1f4e] hover:bg-[#2a2f5e]"
          data-testid="add-contract-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Contract
        </Button>
      </div>

      {/* New Contract Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">New Contract</h2>
          <ContractForm />
        </div>
      )}

      {/* Edit Contract Modal */}
      {editingContract && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Edit Contract</h2>
              <button onClick={() => { setEditingContract(null); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <ContractForm isEdit={true} />
          </div>
        </div>
      )}

      {/* Contracts List */}
      <div className="space-y-3">
        {contracts.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No contracts yet. Create your first contract to start invoicing.</p>
          </div>
        ) : (
          contracts.map((contract) => {
            const siteInfo = getSiteWithClient(contract.site_id);
            const totalPrice = calculateTotalPrice(contract);
            const hasLineItems = (contract.line_items || []).length > 0;
            
            return (
              <div key={contract.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#1a1f4e]/10 rounded-lg flex items-center justify-center mt-1">
                      <Building2 className="w-5 h-5 text-[#1a1f4e]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-medium text-gray-900">{siteInfo.site_name}</p>
                        {getStatusBadge(contract.status)}
                        {contract.scheduling_paused && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 flex items-center gap-1">
                            <CalendarOff className="w-3 h-3" />
                            Scheduling Paused
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{siteInfo.company_name}</p>
                      <p className="text-sm text-gray-400 mt-1">{contract.service_description}</p>
                      
                      {/* Show line items if any */}
                      {hasLineItems && (
                        <div className="mt-2 space-y-1">
                          {contract.line_items.map((item, idx) => (
                            <p key={idx} className="text-xs text-gray-500">
                              + {item.description}: £{item.unit_price.toFixed(2)} × {item.default_quantity}
                            </p>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-lg font-semibold text-[#1a1f4e] mt-2">
                        £{totalPrice.toFixed(2)}/month
                        {hasLineItems && <span className="text-xs font-normal text-gray-500 ml-1">(variable)</span>}
                        {contract.vat_applicable && <span className="text-sm font-normal text-gray-500"> + VAT</span>}
                      </p>
                      
                      {/* Completed Cleans Log */}
                      {contract.status === 'active' && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              Completed Cleans
                              {(contract.completed_cleans || []).length > 0 && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                  {(contract.completed_cleans || []).length}
                                </span>
                              )}
                            </p>
                          </div>
                          
                          {/* Show completed cleans */}
                          {(contract.completed_cleans || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {contract.completed_cleans.map((date) => (
                                <span 
                                  key={date} 
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs rounded-lg"
                                >
                                  {formatCleanDate(date)}
                                  <button 
                                    onClick={() => handleRemoveClean(contract.id, date)}
                                    className="hover:text-red-600 ml-1"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {/* Add new clean date */}
                          <div className="flex items-center gap-2">
                            <input
                              type="date"
                              value={selectedContract === contract.id ? cleanDate : ''}
                              onChange={(e) => {
                                setSelectedContract(contract.id);
                                setCleanDate(e.target.value);
                              }}
                              onFocus={() => setSelectedContract(contract.id)}
                              className="h-8 px-2 text-sm border border-gray-200 rounded-lg focus:border-[#1a1f4e] outline-none"
                            />
                            <Button
                              size="sm"
                              onClick={() => handleAddClean(contract.id)}
                              disabled={addingClean || !cleanDate || selectedContract !== contract.id}
                              className="h-8 bg-green-600 hover:bg-green-700"
                            >
                              {addingClean && selectedContract === contract.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Plus className="w-3 h-3" />
                              )}
                              Log Clean
                            </Button>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            Logged dates will appear on the invoice when sent
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Edit Button */}
                    <button
                      onClick={() => openEditModal(contract)}
                      className="p-2 text-gray-400 hover:text-blue-600"
                      title="Edit contract"
                      data-testid={`edit-contract-${contract.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    
                    {contract.status === 'active' && (
                      <>
                        {/* Send Now - one click invoice */}
                        <button
                          onClick={() => sendNow(contract.id)}
                          disabled={sendingNow === contract.id}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-sm rounded-lg transition-colors"
                          title="Generate and send invoice for current period"
                          data-testid={`send-now-${contract.id}`}
                        >
                          {sendingNow === contract.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Zap className="w-3 h-3" />
                          )}
                          Send Now
                        </button>
                        <Link
                          to={`/admin/invoices?contract=${contract.id}`}
                          className="text-sm text-gray-500 hover:text-[#1a1f4e] hover:underline flex items-center gap-1"
                          data-testid={`generate-invoice-${contract.id}`}
                          title="Generate invoice with custom options"
                        >
                          <Send className="w-3 h-3" />
                          Custom
                        </Link>
                        {/* Scheduling toggle */}
                        <button
                          onClick={() => toggleScheduling(contract.id, !contract.scheduling_paused)}
                          className={`p-2 ${contract.scheduling_paused ? 'text-orange-500 hover:text-green-600' : 'text-gray-400 hover:text-orange-600'}`}
                          title={contract.scheduling_paused ? 'Resume auto-scheduling' : 'Pause auto-scheduling'}
                        >
                          {contract.scheduling_paused ? <Calendar className="w-4 h-4" /> : <CalendarOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => updateStatus(contract.id, 'paused')}
                          className="p-2 text-gray-400 hover:text-yellow-600"
                          title="Pause contract"
                        >
                          <Pause className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {contract.status === 'paused' && (
                      <button
                        onClick={() => updateStatus(contract.id, 'active')}
                        className="p-2 text-gray-400 hover:text-green-600"
                        title="Activate contract"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                    {contract.status !== 'cancelled' && (
                      <button
                        onClick={() => updateStatus(contract.id, 'cancelled')}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Cancel contract"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                    {/* Delete Button */}
                    <button
                      onClick={() => handleDelete(contract.id)}
                      disabled={deleting === contract.id}
                      className="p-2 text-gray-400 hover:text-red-600"
                      title="Delete contract permanently"
                      data-testid={`delete-contract-${contract.id}`}
                    >
                      {deleting === contract.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ContractList;
